import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import api from '../services/api'
import Reveal from '../components/landing/Reveal'
import CinematicNav from '../components/landing/CinematicNav'
import CinematicFooter from '../components/landing/CinematicFooter'
import LoadingSpinner from '../components/LoadingSpinner'

const SITE = 'https://synergymultimediaacademy.com'

export default function CreativeToolPage() {
  const { slug } = useParams()
  const [tool, setTool] = useState(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [error, setError] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setNotFound(false)
    setError(false)
    setTool(null)
    api.get(`/creative-tools/${slug}`)
      .then(({ data }) => { if (!cancelled) setTool(data.tool) })
      .catch((err) => {
        if (cancelled) return
        if (err.response?.status === 404) setNotFound(true)
        else setError(true)
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [slug])

  return (
    <div className="cinema-page min-h-screen selection:bg-cinema-glow/30 selection:text-white">
      <Helmet>
        <title>{tool?.meta_title || 'Creative Tools | Synergy Multimedia Academy'}</title>
        {tool?.meta_description && <meta name="description" content={tool.meta_description} />}
        {tool && <link rel="canonical" href={`${SITE}/creative-tools/${tool.slug}`} />}
        {tool && <meta property="og:title" content={tool.meta_title || tool.name} />}
        {tool?.meta_description && <meta property="og:description" content={tool.meta_description} />}
        {tool && <meta property="og:url" content={`${SITE}/creative-tools/${tool.slug}`} />}
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="cinema-grain" aria-hidden="true" />
      <CinematicNav scrolled={scrolled} navOverHero={!scrolled} onNavigate={() => {}} />

      {loading ? (
        <div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>
      ) : notFound ? (
        <main className="pt-36 pb-24 max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
          <p className="text-xs uppercase tracking-[0.22em] text-cinema-gold font-sans">Creative Tools</p>
          <h1 className="mt-4 font-display text-4xl text-white font-light">This tool is not published.</h1>
          <p className="mt-4 text-sm text-cinema-muted font-sans max-w-xl">
            It may still be forthcoming, or it may not have a public page yet.
          </p>
          <Link to="/creative-tools" className="cinema-btn-text mt-8 text-cinema-gold">
            Back to Creative Tools
          </Link>
        </main>
      ) : error ? (
        <main className="pt-36 pb-24 max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
          <h1 className="font-display text-4xl text-white font-light">This page could not be loaded.</h1>
          <button type="button" onClick={() => window.location.reload()} className="cinema-btn-ghost mt-8">
            Try again
          </button>
        </main>
      ) : tool && (
        <main>
          <header className="pt-28 pb-12 sm:pt-36 sm:pb-16 border-b border-white/10">
            <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
              <Reveal>
                <p className="text-[0.7rem] uppercase tracking-[0.28em] text-cinema-gold font-sans font-semibold">
                  <Link to="/creative-tools" className="hover:text-white transition-colors">SMA Creative Tools</Link>
                  <span className="text-cinema-muted"> / {tool.category_name}</span>
                </p>
                <h1 className="mt-5 font-display text-5xl sm:text-7xl text-white font-light tracking-tight">
                  {tool.name}
                </h1>
                <p className="mt-4 max-w-3xl font-display text-xl sm:text-3xl text-cinema-soft font-light">
                  {tool.headline}
                </p>
                <p className="mt-6 max-w-2xl text-sm sm:text-base text-cinema-soft/85 leading-relaxed font-sans">
                  {tool.short_description}
                </p>
              </Reveal>

              <div className="mt-10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 border-t border-white/10 pt-6">
                <dl className="flex flex-wrap gap-x-8 gap-y-3">
                  <div>
                    <dt className="text-[0.65rem] uppercase tracking-[0.18em] text-cinema-muted font-sans">Status</dt>
                    <dd className="mt-1 text-sm text-white font-sans">{tool.status_label}</dd>
                  </div>
                  {tool.current_release && (tool.status === 'AVAILABLE' || tool.status === 'BETA') && (
                    <div>
                      <dt className="text-[0.65rem] uppercase tracking-[0.18em] text-cinema-muted font-sans">Latest release</dt>
                      <dd className="mt-1 text-sm text-white font-sans">
                        {tool.current_release.version} · {tool.current_release.platform === 'WINDOWS' ? 'Windows' : tool.current_release.platform}
                        {tool.current_release.architecture ? ` · ${tool.current_release.architecture === 'X64' ? 'x64' : tool.current_release.architecture}` : ''}
                      </dd>
                    </div>
                  )}
                  {tool.platform && (
                    <div>
                      <dt className="text-[0.65rem] uppercase tracking-[0.18em] text-cinema-muted font-sans">Platform</dt>
                      <dd className="mt-1 text-sm text-white font-sans">{tool.platform}</dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-[0.65rem] uppercase tracking-[0.18em] text-cinema-muted font-sans">Access</dt>
                    <dd className="mt-1 text-sm text-white font-sans">{tool.pricing_label}</dd>
                  </div>
                </dl>
                <p
                  className="text-[0.7rem] uppercase tracking-[0.22em] text-cinema-gold font-sans"
                  role="status"
                >
                  {tool.release.label}
                  {tool.status !== 'AVAILABLE' ? ' — not available to download' : ''}
                  {tool.distribution?.available && tool.distribution.download_path ? (
                    <a className="ml-4 text-white" href={localStorage.getItem('token') ? tool.distribution.download_path : '/login'}>Download</a>
                  ) : null}
                </p>
              </div>

              {tool.highlights.length > 0 && (
                <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2 border-t border-white/10 pt-6">
                  {tool.highlights.map((item) => (
                    <li key={item} className="text-xs uppercase tracking-[0.16em] text-cinema-soft font-sans">
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </header>

          {tool.problem_points.length > 0 && (
            <section className="border-b border-white/10" aria-labelledby="why-it-exists">
              <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-14 sm:py-20 grid lg:grid-cols-12 gap-10">
                <div className="lg:col-span-4">
                  <p className="text-xs uppercase tracking-[0.22em] text-cinema-gold font-sans font-semibold">01 / Problem</p>
                  <h2 id="why-it-exists" className="mt-4 font-display text-3xl sm:text-4xl text-white font-light">
                    Why {tool.name} exists
                  </h2>
                  {tool.problem_intro && (
                    <p className="mt-4 text-sm text-cinema-muted font-sans leading-relaxed">{tool.problem_intro}</p>
                  )}
                </div>
                <ol className="lg:col-span-8 border-t border-white/10">
                  {tool.problem_points.map((point, index) => (
                    <li key={point} className="grid grid-cols-[3rem_1fr] gap-4 py-4 border-b border-white/10">
                      <span className="text-[0.65rem] font-mono text-cinema-gold tracking-[0.16em]">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <span className="text-sm sm:text-base text-white font-sans">{point}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </section>
          )}

          {tool.workflow_steps.length > 0 && (
            <section className="border-b border-white/10" aria-labelledby="how-it-works">
              <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-14 sm:py-20">
                <p className="text-xs uppercase tracking-[0.22em] text-cinema-gold font-sans font-semibold">02 / Method</p>
                <h2 id="how-it-works" className="mt-4 font-display text-3xl sm:text-4xl text-white font-light">
                  How {tool.name} works
                </h2>
                {tool.workflow_intro && (
                  <p className="mt-4 max-w-2xl text-sm sm:text-base text-cinema-soft font-sans leading-relaxed">
                    {tool.workflow_intro}
                  </p>
                )}
                <ol className="mt-10 border-t border-white/10">
                  {tool.workflow_steps.map((step) => (
                    <li key={step.label} className="grid sm:grid-cols-[4rem_1fr] gap-2 sm:gap-6 py-5 border-b border-white/10">
                      <span className="text-[0.65rem] font-mono text-cinema-gold tracking-[0.16em]">
                        {String(step.order).padStart(2, '0')}
                      </span>
                      <span className="font-display text-2xl text-white font-light">{step.label}</span>
                    </li>
                  ))}
                </ol>
                {tool.connection_note && (
                  <p className="mt-8 max-w-2xl text-sm text-cinema-muted font-sans leading-relaxed">
                    {tool.connection_note}
                  </p>
                )}
              </div>
            </section>
          )}

          {tool.plans.length > 0 && (
            <section className="border-b border-white/10" aria-labelledby="tool-access">
              <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-14 sm:py-20">
                <p className="text-xs uppercase tracking-[0.22em] text-cinema-gold font-sans font-semibold">03 / Access</p>
                <h2 id="tool-access" className="mt-4 font-display text-3xl sm:text-4xl text-white font-light">
                  Choose how you work
                </h2>
                <p className="mt-4 max-w-2xl text-sm text-cinema-muted font-sans">
                  Prices are not published yet. These are the ways {tool.name} is being prepared to be licensed.
                </p>
                <div className="mt-10 border-t border-white/10">
                  {tool.plans.map((plan) => (
                    <div key={plan.model} className="grid md:grid-cols-12 gap-3 md:gap-8 py-6 border-b border-white/10">
                      <h3 className="md:col-span-4 font-display text-2xl text-white font-light">{plan.name}</h3>
                      <p className="md:col-span-5 text-sm text-cinema-soft font-sans">{plan.summary}</p>
                      <p className="md:col-span-3 text-[0.65rem] uppercase tracking-[0.18em] text-cinema-muted font-sans md:text-right">
                        {plan.availability_label}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {tool.learning_topics.length > 0 && (
            <section className="border-b border-white/10" aria-labelledby="learn-tool">
              <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-14 sm:py-20">
                <p className="text-xs uppercase tracking-[0.22em] text-cinema-gold font-sans font-semibold">04 / Learning</p>
                <h2 id="learn-tool" className="mt-4 font-display text-3xl sm:text-4xl text-white font-light">
                  {tool.learning_title || `Learn ${tool.name}`}
                </h2>
                <p className="mt-4 max-w-2xl text-sm text-cinema-muted font-sans">
                  Related study at SMA. Courses are not linked yet. The topics are ready for that connection.
                </p>
                <ul className="mt-10 border-t border-white/10">
                  {tool.learning_topics.map((topic) => (
                    <li key={topic.title} className="py-5 border-b border-white/10">
                      <p className="font-display text-xl text-white font-light">{topic.title}</p>
                      {topic.summary && (
                        <p className="mt-1 text-sm text-cinema-muted font-sans">{topic.summary}</p>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          )}

          {tool.project_connection && (
            <section aria-labelledby="use-on-project">
              <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-14 sm:py-20">
                <p className="text-xs uppercase tracking-[0.22em] text-cinema-gold font-sans font-semibold">05 / Practice</p>
                <h2 id="use-on-project" className="mt-4 font-display text-3xl sm:text-4xl text-white font-light">
                  {tool.project_title || 'Use it on a project'}
                </h2>
                <p className="mt-4 max-w-2xl text-sm sm:text-base text-cinema-soft font-sans leading-relaxed">
                  {tool.project_connection}
                </p>
                <Link to="/project-lab" className="cinema-btn-text mt-8 text-cinema-gold">
                  Explore Project Lab
                  <span aria-hidden="true">→</span>
                </Link>
              </div>
            </section>
          )}
        </main>
      )}

      <CinematicFooter contactEmail="info@synergymultimedia.co.ug" />
    </div>
  )
}
